import httpClient, { IRequest, IResponse } from '../../../apiService/httpClient';
import { IProjectInfoForm, ICountry, ISiteEnvLevel } from '../../pages/ProjectInfo/IProjectInfo';
import { IProjectMacro2Request, IMacro2ProjectPutData, IMacro1ProjectPutData, IContainerYardProjectPutData, IDynamicProjectPutData } from '../IApiRequest';
import {
    IMacro2ProjectOutputData,
    IMacro2ProjectGetData,
    IMacro1ProjectOutputData,
    IMacro1ProjectGetData,
    IContainerYardProjectGetData,
    IContainerYardProjectOutputData,
    IDynamicProjectOutputData,
    IDynamicProjectGetData,
    IBlobFileResponse,
    IDesigner,
    IPaginatedResponse,
    IProjectBaseResponse,
    IProjectMacro1Response,
    IProjectMacro2Response,
    ICoordinateCountry,
    IProject,
    IGeogridFamily,
} from '../IApiResponse';

const BeMaccaferriStoreProvider = () => {
    const _baseURL = process.env.REACT_APP_STORE_BASE_URL as string;

    const _createRequest = <D>(_pathname: string, _queryString?: string, _data?: D): IRequest => {
        return {
            url: _queryString ? _pathname + '?' + _queryString : _pathname,
            baseURL: _baseURL,
            data: _data,
        };
    };

    //project------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

    const getCountryByCoordinates = async (latitude: number, longitude: number): Promise<ICoordinateCountry> => {
        const nominatimResponse = fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&zoom=3&format=json&extratags=1`);

        try {
            // Check if the user's IP address is Italian
            // Usa ip-api.com per ottenere la posizione basata sull'IP
            const ipCountryResponse = await fetch('https://func-iplocation-prd-we-001.azurewebsites.net/api/IPGeolocationAndWhoisLookup');
            const ipCountryData = await ipCountryResponse.json();
            // Parse the ipGeolocation string into an object
            const ipGeolocationData = JSON.parse(ipCountryData.ipGeolocation);
            const ipCountry = ipGeolocationData.country_code;
            if (ipCountry === 'IT') {
                return {
                    extratags: { 'ISO3166-1:alpha2': 'IT' },
                } as ICoordinateCountry;
            } else {
                // Fetch the coordinate-based country information
                const coordinateCountry = (await (await nominatimResponse).json()) as ICoordinateCountry;
                return coordinateCountry;
            }
        } catch (error) {
            const coordinateCountry = (await (await nominatimResponse).json()) as ICoordinateCountry;
            return coordinateCountry;
        }
    };

    // TODO: Replace with real backend endpoint when available
    // Endpoint format: GET /api/geogrid-families?country={countryCode}
    // Expected response: IGeogridFamily[]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getAvailableGeogridFamiliesAsync = async (countryCode: string): Promise<IResponse<IGeogridFamily[]>> => {
        // MOCK IMPLEMENTATION - Remove this when backend is ready
        // Simulates API delay (100ms)
        await new Promise(resolve => setTimeout(resolve, 100));

        // TEMPORARY FIX: Return empty array to bypass geolocation filtering
        // This allows all 138 products to be shown regardless of country
        // When backend endpoint is ready, re-enable country-specific filtering
        //
        // Previous behavior (caused bug when combined with restrictive applicableProjects):
        // - IT: Only MacGrid WG and ParaGrid (64 products)
        // - Other: All families (138 products)
        //
        // Bug: IT users with paved projects saw empty dropdowns because:
        //   1. Mock limited to 2 families (MacGrid WG, ParaGrid)
        //   2. Those families didn't have "paved" in applicableProjects
        //   → Result: 0 products
        return {
            data: [],
        } as unknown as IResponse<IGeogridFamily[]>;
    };

    //TODO: rimettere la paginazione
    //?PageNumber=${pageNumber}&PageSize=${pageSize}
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // ✅ FIXED: Use /api/Project/projects endpoint (cloud storage)
    // Backend returns plain array, so we wrap it in IPaginatedResponse format
    const getProjectsAsync = async (pageNumber: number, pageSize: number): Promise<IResponse<IPaginatedResponse<IProjectBaseResponse>>> => {
        const response = await httpClient.getAsync<IProjectBaseResponse[]>(_createRequest(`Project/projects`));

        // Transform plain array response to paginated format expected by frontend
        const items = Array.isArray(response.data) ? response.data : [];
        const totalCount = items.length;

        return {
            ...response,
            data: {
                items,
                pageNumber: pageNumber,
                totalPages: Math.ceil(totalCount / pageSize),
                totalCount,
                hasPreviousPage: pageNumber > 1,
                hasNextPage: false, // Backend doesn't support pagination yet
            },
        };
    };

    const getProjectCountriesAsync = async (countryName?: string): Promise<IResponse<ICountry[]>> =>
        httpClient.getAsync(_createRequest(`v1/macroprojects/countries${countryName ? `?countryName=${countryName}` : ''}`));

    const getProjectSiteEnvLevelsAsync = async (lang?: string): Promise<IResponse<ISiteEnvLevel[]>> => httpClient.getAsync(_createRequest(`v1/macroprojects/site-environment-levels?lang=${lang}`));
    const getProjectsByDesignerAsync = async (designer: number, pageNumber: number, pageSize: number): Promise<IResponse<IPaginatedResponse<IProjectBaseResponse>>> =>
        httpClient.getAsync(_createRequest(`Store/Designer/GetAllProjectsByDesigner/${designer}?PageNumber=${pageNumber}&PageSize=${pageSize}`));

    const getProjectBaseAsync = async (id: number): Promise<IResponse<IProjectBaseResponse>> => httpClient.getAsync(_createRequest(`Store/Company/GetProjectBase/${id}`));

    const projectAvailableByAuthorAsync = async (id: number, author: string): Promise<IResponse<boolean>> =>
        httpClient.getAsync(_createRequest(`Store/Company/ProjectAvailableByAuthor?projectId=${id}&author=${author}`));

    const getMacro1ProjectByIdAsync = async (id: number): Promise<IResponse<IProjectMacro1Response>> => httpClient.getAsync(_createRequest(`Store/Designer/GetMacro1ProjectById/${id}`));

    //const getMacro2ProjectByIdAsync = async (id: number): Promise<IResponse<IProjectMacro2Response>> => httpClient.getAsync(_createRequest(`Store/Designer/GetMacro1ProjectById/${id}`));
    //GET ContainerYard Project
    const getContainerYardProjectAsync = async (id: string): Promise<IResponse<IContainerYardProjectGetData>> => httpClient.getAsync(_createRequest(`containerYard/${id}`));
    const putContainerYardProjectAsync = async (projectData: IContainerYardProjectPutData): Promise<IResponse<IContainerYardProjectOutputData>> =>
        httpClient.putAsync(_createRequest(`containeryard/${projectData.id}`, '', projectData));
    const putDynamicProjectAsync = async (projectData: IDynamicProjectPutData): Promise<IResponse<IDynamicProjectOutputData>> =>
        httpClient.putAsync(_createRequest(`project_results/${projectData.id}`, '', projectData));
    //GET Macro1 Project
    const getMacro1ProjectAsync = async (id: string): Promise<IResponse<IMacro1ProjectGetData>> => httpClient.getAsync(_createRequest(`v1/macro1/${id}`));
    // ✅ FIXED: Use /api/Project/projects/{id} endpoint (cloud storage)
    const getDynamicProjectAsync = async (id: string): Promise<IResponse<IDynamicProjectGetData>> => httpClient.getAsync(_createRequest(`Project/projects/${id}`));
    //PUT Macro1 Project
    const putMacro1ProjectAsync = async (projectData: IMacro1ProjectPutData): Promise<IResponse<IMacro1ProjectOutputData>> =>
        httpClient.putAsync(_createRequest(`v1/macro1/${projectData.id}`, '', projectData));

    //GET Macro2 Project
    const getMacro2ProjectAsync = async (id: string): Promise<IResponse<IMacro2ProjectGetData>> => httpClient.getAsync(_createRequest(`v1/macro2/${id}`));
    //PUT Macro2 Project
    const putMacro2ProjectAsync = async (projectData: IMacro2ProjectPutData): Promise<IResponse<IMacro2ProjectOutputData>> =>
        httpClient.putAsync(_createRequest(`v1/macro2/${projectData.id}`, '', projectData));

    // Unified endpoint that handles both create and update automatically
    const saveMaccaProjectAsync = async (project: IProject): Promise<IResponse<IProjectBaseResponse>> => {
        const cleanPayload = _cleanProjectPayloadForApi(project);
        // Use unified save endpoint - backend determines if create or update
        return httpClient.postAsync(_createRequest('macca-projects/save', '', cleanPayload));
    };

    // Legacy function for backward compatibility - now uses unified endpoint
    const addProjectAsync = async (project: IProject): Promise<IResponse<IProjectBaseResponse>> => {
        return saveMaccaProjectAsync(project);
    };

    // Helper function to clean project payload for Maccaferri API (based on Swagger documentation)
    const _cleanProjectPayloadForApi = (project: IProject) => {
        return {
            guid: project.guid,
            projectName: project.projectName,
            idApp: Number(process.env.REACT_APP_ID_APP) || 42,
            idUser: project.idUser,
            creationDate: project.creationDate,
            lastModified: project.lastModified,
            lastSeen: project.lastSeen,
            status: project.status,
            sharedUsers: project.sharedUsers,
            projectInfo: {
                coordinates: project.projectInfo?.coordinates,
                projectOwner: project.projectInfo?.projectOwner,
                designerFirmName: project.projectInfo?.designerFirmName,
                designerFirmCountry: project.projectInfo?.designerFirmCountry,
                productCountry: project.projectInfo?.productCountry,
                products: project.projectInfo?.products,
                data: project.projectInfo?.data,
            },
        };
    };

    // Legacy function for backward compatibility - now uses unified endpoint
    const updateMaccaProjectAsync = async (project: IProject): Promise<IResponse<IProjectBaseResponse>> => {
        return saveMaccaProjectAsync(project);
    };

    const putProjectAsync = async (project: IProjectInfoForm): Promise<IResponse<IProjectBaseResponse>> => httpClient.putAsync(_createRequest('v1/macroprojects/' + project.projectId, '', project));

    const updateProjectAsync = async (project: IProjectMacro2Request): Promise<IResponse<IProjectMacro2Response>> => httpClient.putAsync(_createRequest('Store/Company/UpdateProjectCBP', '', project));

    const deleteProjectByIdAsync = async (id: number): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`v1/macroprojects/${id}`));

    //designer------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
    const getDesignerByIdAsync = async (id: number): Promise<IResponse<IDesigner>> => httpClient.getAsync(_createRequest(`Store/Designer/GetDesigner/${id}`));

    const getDesignerByNameAsync = async (designerFirm: string): Promise<IResponse<IDesigner>> => httpClient.getAsync(_createRequest(`Store/Designer/GetDesignerByName/${designerFirm}`));

    const addDesignerAsync = async (designer: IDesigner): Promise<IResponse<boolean>> => httpClient.postAsync(_createRequest(`Store/Designer/AddDesigner`, '', designer));

    //blob------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

    const getAllDocumentsAsync = async (): Promise<IResponse<IBlobFileResponse[]>> => httpClient.getAsync(_createRequest(`Store/Blob/GetAllDocuments`));

    const uploadDocumentAsync = async (file: FormData): Promise<IResponse<boolean>> => httpClient.putAsync(_createRequest('Store/Blob/UploadWithFilename', '', file));

    const deleteDocumentAsync = async (name: string): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`Store/Blob/Delete/${name}`));

    //auth------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
    // ✅ UPDATED: Use new /api/auth/login endpoint (backend AuthController)
    const generateToken = async (): Promise<IResponse<string>> =>
        /*
        TODO: DA RIMETTERE PER TESTARE IN PRODUZIONE
                httpClient.postAsync(
            _createRequest('v1/Token/generateLocalhost', '', {
                appId: Number(process.env.REACT_APP_ID_APP) || 42,
                idUser: 1203,
                authToken: '74f1106f-93a2-4417-9e1b-247a87132756',
                isAdmin: false,
            })
        );
        */

        httpClient.postAsync(
            _createRequest('auth/login', '', {
                Username: 'test',
                Password: 'test',
            })
        );

    return {
        generateToken,
        getProjectsAsync,
        getProjectCountriesAsync,
        getProjectSiteEnvLevelsAsync,
        getProjectsByDesignerAsync,
        projectAvailableByAuthorAsync,
        deleteProjectByIdAsync,
        getProjectBaseAsync,
        getContainerYardProjectAsync,
        putContainerYardProjectAsync,
        getMacro1ProjectByIdAsync,
        //getMacro2ProjectByIdAsync,
        getMacro1ProjectAsync,
        putMacro1ProjectAsync,
        getMacro2ProjectAsync,
        putMacro2ProjectAsync,
        saveMaccaProjectAsync,
        addProjectAsync,
        updateMaccaProjectAsync,
        putProjectAsync,
        updateProjectAsync,
        getDesignerByIdAsync,
        getDesignerByNameAsync,
        addDesignerAsync,
        getAllDocumentsAsync,
        uploadDocumentAsync,
        deleteDocumentAsync,
        getCountryByCoordinates,
        getAvailableGeogridFamiliesAsync,
        putDynamicProjectAsync,
        getDynamicProjectAsync,
    };
};

export default BeMaccaferriStoreProvider();
